import React, { useState, useEffect, useMemo } from 'react';
import { parseLayerData } from '../utils/layerUtils';
import { DEPRECATED_LAYERS } from '../utils/constants';
import SearchFilter from './SearchFilter';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import NoResults from './NoResults';
import LayerTable from './LayerTable';

/**
 * Main layer list component that fetches and displays layers
 * @param {Object} props - Component props
 * @param {string} props.region - AWS region to fetch layers from
 * @param {Function} props.showToast - Function to show toast notifications
 * @returns {JSX.Element} Complete layer list with search and filtering
 */
function LayerList({ region, showToast, searchTerm, onSearchChange }) {
  const [allLayers, setAllLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLayers = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `https://${region}.layers.newrelic-external.com/get-layers`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch layers for region ${region}`);
        }
        const data = await response.json();
        
        const parsedLayers = data.Layers
          .filter(layer => !DEPRECATED_LAYERS.includes(layer.LayerName))
          .map(parseLayerData);
          
        setAllLayers(parsedLayers);
      } catch (err) {
        setError(err.message);
        setAllLayers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLayers();
  }, [region]);

  const groupedAndFilteredLayers = useMemo(() => {
    const keywords = searchTerm
      .toLowerCase()
      .split(' ')
      .filter(word => word);

    const filtered = allLayers.filter(layer => {
      if (keywords.length === 0) {
        return true;
      }

      const searchableContent = [
        layer.runtime,
        layer.runtimeVersion,
        layer.architecture,  
      ].join(' ').toLowerCase();

      return keywords.every(keyword => searchableContent.includes(keyword));
    });

    return filtered.reduce((acc, layer) => {
      const { runtime } = layer;
      if (!acc[runtime]) {
        acc[runtime] = [];
      }
      acc[runtime].push(layer);
      return acc;
    }, {});
  }, [allLayers, searchTerm]);

  const handleCopy = async (text, message) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`Copied: ${text}`, 'success');
    } catch (err) {
      console.error('Failed to copy text: ', err);
      showToast('Failed to copy ARN', 'error');
    }
  };

  if (loading) {
    return <LoadingSpinner region={region} />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }
  
  const runtimes = Object.keys(groupedAndFilteredLayers);

  return (
    <>
      {runtimes.length === 0 && !loading && (
        <NoResults searchTerm={searchTerm} onSearchChange={onSearchChange} />
      )}
      
      {runtimes.length > 0 && (
        <LayerTable groupedLayers={groupedAndFilteredLayers} onCopy={handleCopy} />
      )}
    </>
  );
}

export default LayerList;
